import { Input } from './Input';
import { Renderer } from './Renderer';
import { StateManager } from './StateManager';
import { MenuState } from './states/MenuState';
import { PlayState } from './states/PlayState';
import { PauseState } from './states/PauseState';
import { GameOverState } from './states/GameOverState';
import { SoundManager } from './audio/SoundManager';
import { AudioManager } from './systems/AudioManager';

// Main Game class - coordinates all systems
export class Game {
  private canvas: HTMLCanvasElement;
  private input: Input;
  private renderer: Renderer;
  private stateManager: StateManager;
  private soundManager: SoundManager;
  private audioManager: AudioManager;
  private lastTime: number = 0;
  private running: boolean = false;

  // Game settings
  public readonly CANVAS_WIDTH = 720;
  public readonly CANVAS_HEIGHT = 960;

  constructor(canvas: HTMLCanvasElement, audioManager: AudioManager) {
    this.canvas = canvas;
    this.canvas.width = this.CANVAS_WIDTH;
    this.canvas.height = this.CANVAS_HEIGHT;

    this.input = new Input(canvas);
    this.renderer = new Renderer(canvas);
    this.stateManager = new StateManager();
    this.soundManager = new SoundManager();
    this.audioManager = audioManager;

    this.initializeStates();
    
    // Initialize audio on first user interaction
    canvas.addEventListener('click', () => {
      this.soundManager.init();
    }, { once: true });
  }

  private initializeStates(): void {
    // Register all game states
    this.stateManager.registerState('menu', new MenuState(this));
    this.stateManager.registerState('play', new PlayState(this));
    this.stateManager.registerState('pause', new PauseState(this));
    this.stateManager.registerState('gameover', new GameOverState(this));

    // Start with menu
    this.stateManager.changeState('menu');
  }

  public start(): void {
    this.running = true;
    this.lastTime = performance.now();
    this.loop();
  }

  private loop = (): void => {
    if (!this.running) return;

    const currentTime = performance.now();
    const deltaTime = (currentTime - this.lastTime) / 1000; // Convert to seconds
    this.lastTime = currentTime;

    this.update(deltaTime);
    this.render();

    requestAnimationFrame(this.loop);
  };

  private update(deltaTime: number): void {
    // Cap delta time to prevent huge jumps
    const cappedDelta = Math.min(deltaTime, 0.1);
    this.renderer.updateScreenShake(cappedDelta);
    this.stateManager.update(cappedDelta);
  }

  private render(): void {
    const ctx = this.renderer.getContext();
    ctx.save();
    this.renderer.applyScreenShake();
    this.renderer.clear('#0a0a0f');
    this.stateManager.render(ctx);
    ctx.restore();
  }

  public stop(): void {
    this.running = false;
  }

  // Getters for subsystems
  public getInput(): Input {
    return this.input;
  }

  public getRenderer(): Renderer {
    return this.renderer;
  }

  public getStateManager(): StateManager {
    return this.stateManager;
  }

  public getSoundManager(): SoundManager {
    return this.soundManager;
  }

  public getAudioManager(): AudioManager {
    return this.audioManager;
  }

  public getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }
}
